import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { HelpCircle, History } from 'lucide-react';
import type { HistoryTab } from '@/features/promo/list-visibility.js';

interface BonusSectionHeaderProps {
  title: string;
  subtitle?: string;
  /** Shows the "how it works" pill; pass only where the tab has a tutorial. */
  onHowTo?: () => void;
  /** Shows the history button, opening the history screen for this tab. */
  historyTab?: HistoryTab;
}

export function BonusSectionHeader({ title, subtitle, onHowTo, historyTab }: BonusSectionHeaderProps) {
  const { t } = useTranslation('promo');

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-xl font-extrabold tracking-tight text-[var(--text-primary)]">{title}</h2>
        {(subtitle || onHowTo) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            {subtitle && <p className="text-[12px] text-[var(--text-secondary)]">{subtitle}</p>}
            {onHowTo && (
              <button
                type="button"
                onClick={onHowTo}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-elevated)] hover:bg-[var(--primary)] hover:text-[var(--primary-on)] text-[var(--text-secondary)] px-2.5 py-1 text-[11.5px] font-semibold transition-colors"
              >
                <HelpCircle size={13} aria-hidden />
                {t('tutorial.reopen').replace('(?) ', '')}
              </button>
            )}
          </div>
        )}
      </div>
      {historyTab && <HistoryButton tab={historyTab} />}
    </div>
  );
}

function HistoryButton({ tab }: { tab: HistoryTab }) {
  const { t } = useTranslation('promo');
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/promos/history', search: { tab } })}
      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[var(--primary)]/50 bg-[var(--primary)]/10 hover:bg-[var(--primary)]/20 px-2.5 py-1.5 text-[12px] font-semibold text-[var(--primary)] transition-colors"
    >
      <History size={14} aria-hidden />
      {t('hub.history')}
    </button>
  );
}
